import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import { useConfig } from '../contexts/ConfigContext.js';
import { useLeagueData } from '../contexts/LeagueDataContext.js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase.js';

const STAT_KEYS = ['points', 'rebounds', 'assists', 'steals', 'blocks'];
const STAT_LABELS = {
  points: 'Points',
  rebounds: 'Rebounds',
  assists: 'Assists',
  steals: 'Steals',
  blocks: 'Blocks',
};
const STAT_ABBV = {
  points: 'PTS',
  rebounds: 'REB',
  assists: 'AST',
  steals: 'STL',
  blocks: 'BLK',
};

const Stats = () => {
  const config = useConfig();
  const { teams, players: cachedPlayers, games: cachedGames, dataLoaded } = useLeagueData(['teams', 'players', 'games']);

  const [records, setRecords] = useState({});
  const [recordsLoaded, setRecordsLoaded] = useState(false);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, user => setCurrentUser(user));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    loadRecords();
  }, []);

  useEffect(() => {
    if (dataLoaded) {
      setPlayers(cachedPlayers);
      setGames(cachedGames);
    }
  }, [dataLoaded, cachedPlayers, cachedGames]);

  useEffect(() => {
    if (recordsLoaded && dataLoaded) {
      setDataReady(true);
    }
  }, [recordsLoaded, dataLoaded]);

  const loadRecords = async () => {
    const recordsSnap = await getDocs(collection(db, 'records'));
    const data = {};
    recordsSnap.forEach(d => { data[d.id] = d.data(); });
    setRecords(data);
    setRecordsLoaded(true);
  };

  const isAdmin = currentUser && config.adminEmails.includes(currentUser.email);

  const updateAllRecords = async () => {
    setUpdating(true);
    setUpdateMessage('');
    try {
      const [gameStatsSnap, gamesSnap] = await Promise.all([
        getDocs(collection(db, 'gameStats')),
        getDocs(collection(db, 'games'))
      ]);

      const allStats = [];
      gameStatsSnap.forEach(d => {
        allStats.push(...(d.data()['stats'] || []));
      });

      const allGames = gamesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const seasons = [...new Set(allGames.map(g => g.season).filter(Boolean))];

      const playedStats = allStats.filter(s => s.status === 'played');

      const dedup = holders => {
        const seen = new Set();
        return holders.filter(h => {
          const k = `${h.playerID}_${h.gameID}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      };

      const buildRecord = (statsSubset) => {
        const rec = {};
        STAT_KEYS.forEach(key => {
          const max = statsSubset.length > 0 ? Math.max(...statsSubset.map(s => s[key] || 0)) : 0;
          rec[key] = max;
          rec[`${key}_holders`] = max > 0
            ? dedup(statsSubset.filter(s => (s[key] || 0) === max).map(s => ({ playerID: s.playerID, gameID: s.gameID })))
            : [];
        });
        return rec;
      };

      const writes = [];

      seasons.forEach(season => {
        const seasonGameIDs = new Set(allGames.filter(g => g.season == season).map(g => g.id));
        const seasonStats = playedStats.filter(s => seasonGameIDs.has(s.gameID));
        writes.push(setDoc(doc(db, 'records', `season_${season}`), buildRecord(seasonStats)));
      });

      writes.push(setDoc(doc(db, 'records', 'league'), buildRecord(playedStats)));

      // Per-player career records
      const playerIDs = [...new Set(playedStats.map(s => s.playerID))];
      playerIDs.forEach(playerID => {
        const playerStats = playedStats.filter(s => s.playerID === playerID);
        const careerRec = {};
        STAT_KEYS.forEach(key => {
          careerRec[key] = Math.max(...playerStats.map(s => s[key] || 0));
        });
        writes.push(setDoc(doc(db, 'records', playerID), careerRec));
      });

      await Promise.all(writes);
      await loadRecords();
      setUpdateMessage('Records updated!');
    } catch (err) {
      console.error('Error updating records:', err);
      setUpdateMessage('Error updating records.');
    } finally {
      setUpdating(false);
    }
  };

  const getPlayerName = (playerID) => {
    const p = players.find(p => p.id === playerID);
    if (!p) return playerID;
    return `${p.firstName} ${p.lastName}`;
  };

  const getGame = (gameID) => games.find(g => g.id === gameID);

  const getTeamName = (teamID) => {
    const t = teams.find(t => t.id === teamID || t.teamID === teamID);
    return t ? t.name : teamID;
  };

  const formatGameLabel = (gameID) => {
    const game = getGame(gameID);
    if (!game) return gameID;
    const date = game.date instanceof Date ? game.date : game.date?.toDate?.() ?? null;
    const dateStr = date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const away = getTeamName(game.awayTeamID);
    const home = getTeamName(game.homeTeamID);
    return dateStr;
    // return `${away} vs. ${home}${dateStr ? ` · ${dateStr}` : ''}`;
  };

  const allSeasonNums = Object.keys(records)
    .filter(k => k.startsWith('season_'))
    .map(k => parseInt(k.replace('season_', '')))
    .sort((a, b) => a - b);

  const renderCell = (rec, statKey) => {
    const value = rec?.[statKey] || 0;
    const holders = rec?.[`${statKey}_holders`] || [];

    // Group holders by gameID
    const byGame = {};
    holders.forEach(h => {
      if (!byGame[h.gameID]) byGame[h.gameID] = [];
      byGame[h.gameID].push(h.playerID);
    });

    const firstGameID = Object.keys(byGame)[0];

    const gamesWithRecords = Object.entries(byGame);

    return (
      <td className="league-table-data-plain user-name stat-record-cell" key={statKey} style={{ textAlign: 'center', verticalAlign: 'top' }}>
        {/* Mobile: just the value as a link to the game */}
        <span className="mobile-only">
          {value
            ? firstGameID
              ? <Link className="unstyled-link" to={`/games/${firstGameID}`}>{value}</Link>
              : value
            : '-'}
        </span>
        {/* Desktop: value + player names + game */}
        <span className="desktop-only">
          { renderRecordValue(value, gamesWithRecords) }
          {gamesWithRecords.map(([gameID, playerIDs]) => (
            <span key={gameID} style={{ display: 'block', fontSize: '0.78rem', marginTop: '0.25rem', lineHeight: '1.4', color: 'gray' }}>
              <Link className="unstyled-link" style={{ color: 'inherit' }} to={`/games/${gameID}`}>
                {formatGameLabel(gameID)}
              </Link>
            </span>
          ))}
        </span>
      </td>
    );
  };

  const renderRecordValue = (value, gamesWithRecords) => {
    if (!value) return '-';
    const count = gamesWithRecords.length;

    if (count > 1) {
      return (
        <span>
          {value}
          <span style={{ fontSize: '0.9rem', color: 'gray', marginLeft: '0.45rem' }}>
            ({count}x)
          </span>
        </span>
      );
    } else {
      return value;
    }
  }

  if (!dataReady) {
    return <div className="Schedule"><LoadingAnimation /></div>;
  }

  return (
    <div className="Schedule">
      <div className="league-content">
        <div className="league-body">
          <p className="league-title-standard-secondary text-align-center">Single-Game Records</p>

          {isAdmin && (
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <button className="btn btn-dark" onClick={updateAllRecords} disabled={updating}>
                {updating ? 'Updating...' : 'Update Records'}
              </button>
              {updateMessage && (
                <p style={{ marginTop: '0.5rem', color: updateMessage.startsWith('Error') ? 'red' : 'green' }}>
                  {updateMessage}
                </p>
              )}
            </div>
          )}

          <div className="league-table-container scroll-on-overflow">
            <table className="table league-table">
              <thead className="league-table-heading-hole">
                <tr>
                  <th className="league-table-heading hole"><span>SZN</span></th>
                  {STAT_KEYS.map(k => (
                    <th key={k} className="league-table-heading hole"><span>{STAT_ABBV[k]}</span></th>
                  ))}
                </tr>
              </thead>
              <thead className="league-table-heading-total">
                <tr>
                  <th className="league-table-heading-small"><span>SZN</span></th>
                  {STAT_KEYS.map(k => (
                    <th key={k} className="league-table-heading-small"><span>{STAT_ABBV[k]}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allSeasonNums.map(seasonNum => {
                  const rec = records[`season_${seasonNum}`];
                  return (
                    <tr key={seasonNum}>
                      <td className="league-table-data-plain no-wrap"><span>{seasonNum}</span></td>
                      {STAT_KEYS.map(k => renderCell(rec, k))}
                    </tr>
                  );
                })}
                <tr>
                  <td className="league-table-data-plain no-wrap"><strong>All-Time</strong></td>
                  {STAT_KEYS.map(k => renderCell(records['league'], k))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Stats;
